import { useMemo } from 'react';

import { Cluster } from '@cast/types';

import {
  isOverviewQueryUnresolved,
  useOrganizationOverviewQuery,
} from '@cast/shared-cost-data-access';

import { useSearchContextClusters } from '@cast/features-cluster-filters';
import {
  getClustersOverviewRangeIso,
  useClustersOverviewDateRange,
} from '@cast/features-cluster-shared';
import {
  mapOverviewTimeseriesToUtilizationSpark,
  mapOverviewToFleetMetrics,
} from '@cast/features-cluster-shared-utils';

export const useFleetDashboardMetrics = () => {
  const { clusters = [], isLoading: isClustersLoading } =
    useSearchContextClusters<Cluster>();
  const range = useClustersOverviewDateRange();
  const { startTime, endTime } = getClustersOverviewRangeIso(range);
  const {
    data: overview,
    isFetching: isOverviewQueryFetching,
    error: overviewError,
    refetch: refetchOverview,
  } = useOrganizationOverviewQuery({ startTime, endTime });
  const overviewErrorForUi = isOverviewQueryFetching
    ? undefined
    : overviewError;
  const isOverviewLoading = isOverviewQueryUnresolved(
    overview,
    overviewErrorForUi
  );

  const { stats, resources, nodeCosts, merged } = useMemo(
    () => mapOverviewToFleetMetrics(overview, clusters),
    [clusters, overview]
  );

  const { utilizationSparkData, utilizationSparkDomain } = useMemo(
    () =>
      mapOverviewTimeseriesToUtilizationSpark(merged.resourceTimeseries, {
        startTime,
        endTime,
      }),
    [endTime, merged.resourceTimeseries, startTime]
  );

  return {
    stats,
    utilizationSparkData,
    utilizationSparkDomain,
    isSummaryMetricsLoading: isClustersLoading || isOverviewLoading,
    isUtilizationCardLoading: isOverviewLoading,
    resources,
    nodeCosts,
    error: overviewErrorForUi,
    refetch: () => {
      void refetchOverview();
    },
  };
};
